import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraDataTable, SpecraFormField, SpecraHeading, SpecraInlineBanner, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useAuth } from '../../../../contexts/authContext';
import { projectDataClient, proposalFileQAClient } from '../../../../lib/connect/clients';
const disclaimer = 'This tool does not guarantee government acceptance.';
const t = Testids.proposalFileQA;
export function ProposalFileQAPanel({ projectId }) {
    const { user } = useAuth();
    return /*#__PURE__*/ _jsx("section", {
        "aria-label": "Final-file QA",
        "data-testid": t.panel,
        children: /*#__PURE__*/ _jsx(SpecraCard, {
            className: "mb-4",
            children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-4",
                children: [
                    /*#__PURE__*/ _jsx(SpecraHeading, {
                        size: "sm",
                        children: "Final-file QA"
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        children: disclaimer
                    }),
                    user ? /*#__PURE__*/ _jsx(ScopedFileQA, {
                        projectId: projectId,
                        principal: `${user.tenant_id}:${user.id}`
                    }, `${user.tenant_id}:${user.id}:${projectId}`) : /*#__PURE__*/ _jsx(SpecraText, {
                        children: "Sign in to check final files."
                    })
                ]
            })
        })
    });
}
function instruction(check) {
    try {
        const snapshot = JSON.parse(check.instructionSnapshotJson);
        if (snapshot && typeof snapshot === 'object') {
            const fact = snapshot;
            return {
                text: typeof fact.sourceText === 'string' && fact.sourceText ? fact.sourceText : 'Instruction text unavailable',
                provenance: [
                    fact.sourceFilename,
                    fact.sourceBlockIds,
                    fact.sourcePages
                ].filter((v)=>typeof v === 'string' && v).join(' · ')
            };
        }
    } catch  {
    /* Retain the saved result even when its snapshot is unreadable. */ }
    return {
        text: 'Instruction text unavailable',
        provenance: 'Source snapshot could not be read.'
    };
}
function resultLabel(check) {
    if (check.result === 'pass') return 'Pass';
    if (check.result === 'issue') return 'Issue';
    return check.reason.startsWith('unsupported_') ? 'Not checked — unsupported' : 'Not checked';
}
function reasonDescription(reason) {
    if (reason.startsWith('unsupported_instruction:')) return 'This source instruction cannot be checked automatically.';
    if (reason.startsWith('unsupported_scope:')) return 'This instruction has no confirmed mapping to the selected final file.';
    if (reason.startsWith('unsupported_file:')) return 'Automatic checks support only plain-text (.txt) final files.';
    if (reason.startsWith('missing_source:')) return 'Source document and instruction text are required.';
    return reason.replace(/^unsupported_[a-z_]+:\s*/, '');
}
function ScopedFileQA({ projectId, principal }) {
    const [artifactId, setArtifact] = useState('');
    const [fileId, setFile] = useState('');
    const [versionId, setVersion] = useState('');
    const saving = useRef(false);
    const history = useQuery({
        queryKey: [
            'proposalFileQA',
            projectId,
            principal
        ],
        queryFn: ()=>proposalFileQAClient.listProposalFileQA({
                projectId,
                fileId: '',
                versionId: ''
            }),
        retry: false,
        gcTime: 0,
        refetchOnMount: 'always'
    });
    // Fetch under the same principal as QA, only after the service confirms access.
    const files = useQuery({
        queryKey: [
            'projectFiles',
            projectId,
            'fileQA',
            principal
        ],
        queryFn: ()=>projectDataClient.listProjectFiles({
                projectId,
                pagination: {
                    pageSize: 5000
                }
            }),
        enabled: history.isSuccess && history.isFetchedAfterMount,
        retry: false,
        gcTime: 0,
        refetchOnMount: 'always',
        refetchInterval: 5000
    });
    const versions = history.data?.history?.versions ?? [];
    const selectedFile = fileId || versions[0]?.fileId || '';
    const fileVersions = versions.filter((v)=>v.fileId === selectedFile);
    const head = fileVersions.find((v)=>v.current);
    const selectedVersion = versionId ? fileVersions.find((v)=>v.versionId === versionId) : head;
    const checks = (history.data?.history?.checks ?? []).filter((c)=>c.fileId === selectedFile && (!versionId || c.versionId === versionId));
    const runs = (history.data?.history?.runs ?? []).filter((r)=>r.fileId === selectedFile && (!versionId || r.versionId === versionId));
    const refresh = ()=>history.refetch({
            throwOnError: true
        });
    const select = useMutation({
        mutationFn: (replacesVersionId)=>proposalFileQAClient.selectProposalFinalFile({
                projectId,
                artifactId,
                replacesVersionId
            }),
        onSuccess: async (response)=>{
            setFile(response.version?.fileId ?? '');
            setVersion('');
            setArtifact('');
            await refresh();
        }
    });
    const run = useMutation({
        mutationFn: ()=>proposalFileQAClient.runProposalFileQA({
                projectId,
                fileId: selectedFile,
                versionId: selectedVersion?.versionId ?? ''
            }),
        onSuccess: refresh
    });
    const error = history.error || files.error || select.error || run.error;
    const denied = error instanceof ConnectError && (error.code === Code.PermissionDenied || error.code === Code.Unauthenticated);
    const busy = select.isPending || run.isPending;
    const act = async (action)=>{
        if (saving.current) return;
        saving.current = true;
        try {
            await action();
        } catch  {
        /* Actionable error below; do not display raw server content. */ } finally{
            saving.current = false;
        }
    };
    const retry = ()=>{
        select.reset();
        run.reset();
        setArtifact('');
        setVersion('');
        setFile('');
        void history.refetch();
        void files.refetch();
    };
    if (error) return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
        tone: "danger",
        children: [
            denied ? 'You do not have access to file QA.' : 'File QA could not be completed. Retry to refresh file versions and check your selection.',
            /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: retry,
                disabled: busy,
                children: "Retry"
            })
        ]
    });
    if (!history.isFetchedAfterMount || history.isPending || files.isPending) return /*#__PURE__*/ _jsx("output", {
        children: /*#__PURE__*/ _jsx(SpecraText, {
            children: "Loading final-file QA…"
        })
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                tone: "muted",
                children: "Choose an uploaded file to check against extracted source instructions. Only supported plain-text instructions can be checked automatically. Unsupported files and instructions remain Not checked with a reason."
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Uploaded file",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "data-testid": t.artifact,
                    value: artifactId,
                    disabled: busy,
                    onChange: (e)=>setArtifact(e.target.value),
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "Choose an uploaded file"
                        }),
                        (files.data?.files ?? []).map((f)=>/*#__PURE__*/ _jsxs("option", {
                                value: f.fileId,
                                children: [
                                    f.filename,
                                    " · ",
                                    f.fileId
                                ]
                            }, f.fileId))
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                disabled: busy || !artifactId,
                onClick: ()=>void act(()=>select.mutateAsync('')),
                children: "Use as final file"
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Final file",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "data-testid": t.file,
                    value: selectedFile,
                    disabled: busy || !versions.length,
                    onChange: (e)=>{
                        setFile(e.target.value);
                        setVersion('');
                    },
                    children: [
                        !versions.length && /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "No final file selected"
                        }),
                        versions.filter((v)=>v.current).map((v)=>/*#__PURE__*/ _jsxs("option", {
                                value: v.fileId,
                                children: [
                                    v.filename,
                                    " · ",
                                    v.fileId
                                ]
                            }, v.fileId))
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "File version",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "data-testid": t.version,
                    value: versionId,
                    disabled: busy || !fileVersions.length,
                    onChange: (e)=>setVersion(e.target.value),
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "All versions (run on current)"
                        }),
                        fileVersions.map((v)=>/*#__PURE__*/ _jsxs("option", {
                                value: v.versionId,
                                children: [
                                    v.filename,
                                    " · ",
                                    v.versionId,
                                    v.current ? ' · Current' : ' · Previous'
                                ]
                            }, v.versionId))
                    ]
                })
            }),
            selectedVersion && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        children: selectedVersion.filename
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "p",
                        children: [
                            "File: ",
                            selectedFile
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "p",
                        children: [
                            "Artifact: ",
                            /*#__PURE__*/ _jsx("span", {
                                children: selectedVersion.artifactId
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "p",
                        children: [
                            "Version: ",
                            /*#__PURE__*/ _jsx("span", {
                                children: selectedVersion.versionId
                            })
                        ]
                    }),
                    selectedVersion.parentVersionId && /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "p",
                        children: [
                            "Replaces version: ",
                            selectedVersion.parentVersionId
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                disabled: busy || !artifactId || !head,
                onClick: ()=>void act(()=>select.mutateAsync(head?.versionId ?? '')),
                children: "Replace final file"
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": t.run,
                disabled: busy || !selectedVersion?.current,
                onClick: ()=>void act(()=>run.mutateAsync()),
                children: "Run file QA"
            }),
            busy && /*#__PURE__*/ _jsx("output", {
                "data-testid": t.progress,
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    children: run.isPending ? 'Checking final file…' : 'Saving final-file selection…'
                })
            }),
            history.isFetching && /*#__PURE__*/ _jsx("output", {
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    children: "Refreshing file history…"
                })
            }),
            !checks.length && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                tone: "neutral",
                children: [
                    "Nothing was checked.",
                    ' ',
                    runs.length ? 'This run found no source instructions to check. Review the sources and extraction before running again.' : 'Select a final file and run file QA to create a saved result.'
                ]
            }),
            !!checks.length && checks.every((c)=>c.result === 'not_checked') && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "warning",
                children: "Nothing was checked automatically. All instructions were unsupported; review each reason below."
            }),
            /*#__PURE__*/ _jsx(SpecraDataTable, {
                rows: checks,
                getRowId: (c)=>c.id,
                emptyTitle: "No saved checks",
                columns: [
                    {
                        id: 'instruction',
                        header: 'Source instruction',
                        cell: (c)=>/*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        children: instruction(c).text
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        as: "p",
                                        size: "meta",
                                        children: [
                                            "Instruction: ",
                                            c.instructionId,
                                            " · Source: ",
                                            c.sourceDocumentId || 'Unavailable',
                                            " ·",
                                            ' ',
                                            instruction(c).provenance
                                        ]
                                    })
                                ]
                            })
                    },
                    {
                        id: 'result',
                        header: 'Result',
                        cell: (c)=>/*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    c.stale ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                                variant: "warning",
                                                children: "Stale"
                                            }),
                                            /*#__PURE__*/ _jsxs(SpecraText, {
                                                as: "p",
                                                children: [
                                                    "Previous result: ",
                                                    resultLabel(c)
                                                ]
                                            })
                                        ]
                                    }) : /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: c.result === 'pass' ? 'success' : c.result === 'issue' ? 'danger' : 'neutral',
                                        children: resultLabel(c)
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        children: reasonDescription(c.reason)
                                    }),
                                    c.evidence && /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        children: c.evidence
                                    }),
                                    c.stale && /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        children: c.staleReason
                                    })
                                ]
                            })
                    },
                    {
                        id: 'version',
                        header: 'Saved evidence',
                        cell: (c)=>/*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        children: c.versionId
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        as: "p",
                                        children: [
                                            "File: ",
                                            c.fileId,
                                            " · Run: ",
                                            c.runId
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        as: "p",
                                        children: [
                                            "Checked ",
                                            c.checkedAt,
                                            " by ",
                                            c.checkedBy
                                        ]
                                    })
                                ]
                            })
                    }
                ]
            }),
            runs.map((r)=>/*#__PURE__*/ _jsxs(SpecraText, {
                    as: "p",
                    size: "meta",
                    children: [
                        "Run ",
                        r.id,
                        " · Version ",
                        r.versionId,
                        " · ",
                        r.checkedAt,
                        r.stale ? ` · Stale: ${r.staleReason}` : ''
                    ]
                }, r.id))
        ]
    });
}
